import type { ReactNode } from 'react';

import type { Palette } from '@shieldpay/theme-provider-ui/theme/types';

import AlertIcon from '../icons/AlertIcon';
import CorrectIcon from '../icons/CorrectIcon';
import InfoIcon from '../icons/InfoIcon';

import { Box } from '../box/box';
import { Text } from '../text/text';
import { TextList } from '../text-list/text-list';

type Variant = keyof typeof variants;

interface InfoblockBaseProps {
  variant?: Variant;
  id?: string;
  role?: string;
  'data-testid'?: string;
}

export type InfoblockProps =
  | (InfoblockBaseProps & { children: ReactNode; list?: never })
  | (InfoblockBaseProps & { children?: never; list: ReactNode[] })
  | (InfoblockBaseProps & { children: ReactNode; list: ReactNode[] });

const TEST_IDS = {
  INFO_BLOCK: 'infoblock',
  ICON_DEFAULT: 'infoblockIconDefault',
  ICON_INFO: 'infoblockIconInfo',
  ICON_SUCCESS: 'infoblockIconSuccess',
  ICON_WARNING: 'infoblockIconWarning',
  ICON_ERROR: 'infoblockIconError',
};

const variants = {
  default: {
    Component: AlertIcon,
    background: 'neutral100',
    testId: TEST_IDS.ICON_DEFAULT,
  },
  info: {
    Component: InfoIcon,
    background: 'info200',
    testId: TEST_IDS.ICON_INFO,
  },
  success: {
    Component: CorrectIcon,
    background: 'secondary200',
    testId: TEST_IDS.ICON_SUCCESS,
  },
  warning: {
    Component: InfoIcon,
    background: 'warning200',
    testId: TEST_IDS.ICON_WARNING,
  },
  error: {
    Component: AlertIcon,
    background: 'alert200',
    testId: TEST_IDS.ICON_ERROR,
  },
} satisfies Record<
  string,
  Record<string, unknown> & Record<'background', Palette>
>;

export const Infoblock = ({
  children,
  list,
  variant = 'default',
  id,
  role,
  'data-testid': testIdInfoBlock = TEST_IDS.INFO_BLOCK,
}: InfoblockProps) => {
  const { Component, testId, background } = variants[variant];
  return (
    <Box
      background={background}
      stack="row"
      alignContent={{ x: 'left' }}
      padding={{ x: 'base', y: 'baseNeg2' }}
      spacing="baseNeg3"
      data-testid={testIdInfoBlock}
      rounded
    >
      <Component data-testid={testId} type={variant} size="s" />

      <Box
        stack="column"
        spacing="baseNeg4"
        // e.g. id="error-message-for-field-1" and role="alert" is used for field errors.
        id={id}
        role={role}
      >
        {children ? <Text variant="caption150">{children}</Text> : null}

        {list && list.length > 0 ? (
          list.length === 1 && !children ? (
            <Text variant="caption150">{list[0]}</Text>
          ) : (
            <TextList variant="caption150">
              {list.map((item, index) => (
                // eslint-disable-next-line
                <TextList.Item key={index}>{item}</TextList.Item>
              ))}
            </TextList>
          )
        ) : null}
      </Box>
    </Box>
  );
};
